/**
 * useAuth Hook
 * 
 * Manages authentication state and login/logout operations.
 */

import { useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import api, { authApi } from '../services/api';
import { getActiveConversationId, clearActiveConversationId } from '../services/chatSession';
import { adaptUserResponse } from '../adapters';
import { logger } from '../services/logger';
import { CONFIG } from '../config';
import { clearDashboardV2Cache } from './useDashboardV2';
import { handleError } from '../utils/errorHandler';
import { identifyUser, resetUser } from '../services/posthog';
import { withJitter, isTabHidden } from '../utils/polling';

const CREDITS_POLL_INTERVAL_MS = 60_000;

export interface UseAuthReturn {
  user: User | null;
  loading: boolean;
  login: (loggedInUser: User) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (CONFIG.DEBUG_FRONTEND_LOGS) {
        console.info('[auth] checkAuth start', {
          hasAccessToken: authApi.isAuthenticated(),
          href: window.location.href,
        });
      }
      try {
        if (authApi.isAuthenticated()) {
          try {
            const currentUser = await authApi.getCurrentUser();
            if (CONFIG.DEBUG_FRONTEND_LOGS) {
              console.info('[auth] getCurrentUser success', {
                userId: currentUser.id,
                role: currentUser.role,
              });
            }
            const adapted = adaptUserResponse(currentUser);
            setUser(adapted);
            identifyUser(adapted.id, { email: adapted.email, role: adapted.role });
          } catch (error) {
            const handled = handleError(error, {
              fallback: 'Failed to fetch current user',
              logContext: 'useAuth:checkAuth',
              log: !CONFIG.DEBUG_FRONTEND_LOGS,
            });
            if (CONFIG.DEBUG_FRONTEND_LOGS) {
              console.warn('[auth] getCurrentUser failed, forcing logout', {
                error: handled.message,
              });
            }
            // Token is invalid/expired - clear it and force re-login
            // This handles cases where token exists locally but server rejects it
            try { await authApi.logout(); } catch { /* best-effort */ }
          }
        }
      } finally {
        if (CONFIG.DEBUG_FRONTEND_LOGS) {
          console.info('[auth] checkAuth complete');
        }
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // `user.credits_balance`/`credits_included` are only as fresh as the last
  // login/page-load `getCurrentUser()` call — they never update in place
  // after a scan consumes credits or a platform admin grants more. Poll the
  // lightweight subscription endpoint so the sidebar's CreditsChip and the
  // TrialBanner reflect the live balance instead of a stale snapshot.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const refreshCredits = async () => {
      if (isTabHidden()) return;
      try {
        const sub = await api.licensing.getSubscription();
        if (cancelled) return;
        setUser(prev => {
          if (!prev) return prev;
          if (
            prev.credits_balance === sub.credits_balance &&
            prev.credits_included === sub.credits_included &&
            prev.subscription_status === sub.status
          ) {
            return prev;
          }
          return {
            ...prev,
            credits_balance: sub.credits_balance,
            credits_included: sub.credits_included,
            subscription_status: sub.status,
          };
        });
      } catch {
        // Non-fatal: keep showing the last known balance and retry next tick.
      }
    };

    void refreshCredits();
    const interval = setInterval(refreshCredits, withJitter(CREDITS_POLL_INTERVAL_MS));
    const onVisible = () => { if (document.visibilityState === 'visible') void refreshCredits(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // Re-run only when the authenticated user identity changes, not on every
    // user state update (which would otherwise happen from this same effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const login = useCallback(async (loggedInUser: User) => {
    if (CONFIG.DEBUG_FRONTEND_LOGS) {
      console.info('[auth] login state update', {
        userId: loggedInUser.id,
        role: loggedInUser.role,
      });
    }
    setUser(loggedInUser);
    identifyUser(loggedInUser.id, { email: loggedInUser.email, role: loggedInUser.role });
    await logger.log(loggedInUser, 'LOGIN', 'User logged in');
  }, []);

  const logout = useCallback(async () => {
    if (CONFIG.DEBUG_FRONTEND_LOGS) {
      console.info('[auth] logout requested', { userId: user?.id ?? null });
    }
    if (user) {
      await logger.log(user, 'LOGOUT', 'User logged out');
    }
    // End (delete) any active chatbot conversation while the token is still
    // valid — the conversation is ephemeral and must not outlive the session.
    const activeConversationId = getActiveConversationId();
    if (activeConversationId) {
      try {
        await api.chat.endConversation(activeConversationId, 'logout');
      } catch {
        // Best-effort — the server inactivity timeout will reap it otherwise.
      }
      clearActiveConversationId();
    }
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    }
    clearDashboardV2Cache();
    resetUser();
    setUser(null);
  }, [user]);

  return {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  };
}

